import { ChangeDetectorRef, Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '../../../../../services/user.service';
import { IssueReportService } from '../../../../../services/reports.service';
import { toFileUrl } from '../../../../../helpers/file-url';

import {
  AssignableUser,
  assignableUserLabel,
  IssueReport,
  STATUS_ORDER,
  ReportCategory,
  ReportStatus,
} from '../../../../../models/reports';
import { StatusStepper } from '../../../../common/status-stepper/status-stepper';
import { UserAvatar } from '../../../../common/user-avatar/user-avatar';
import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../../../constants/permissions';
export interface ReportsDialogData {
  report: IssueReport;
  currentUserId?: number;
}

const CATEGORY_ICON: Record<ReportCategory, string> = {
  Bug: 'bug_report',
  Outage: 'flash_off',
  'Feature Request': 'lightbulb',
  'Access Issue': 'key',
  Other: 'chat_bubble',
};

const CATEGORY_CLASS: Record<ReportCategory, string> = {
  Bug: 'bug',
  Outage: 'outage',
  'Feature Request': 'feature',
  'Access Issue': 'access',
  Other: 'other',
};

@Component({
  selector: 'app-issue-reports-info',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    StatusStepper,
    HasPermissionDirective,
    UserAvatar,
  ],
  templateUrl: './issue-reports-info.html',
  styleUrl: './issue-reports-info.css',
})
export class IssueReportsInfo implements OnInit {
  userService = inject(UserService);
  reportService = inject(IssueReportService);
  private cdr = inject(ChangeDetectorRef);
  report: IssueReport;
  statuses = STATUS_ORDER;
  permissions = USER_PERMISSIONS;
  statusUpdating = false;
  assigneeUpdating = false;
  assignableUsers: AssignableUser[] = [];
  reportChanged = false;

  constructor(
    private dialogRef: MatDialogRef<ReportsDialogData, IssueReport>,
    @Inject(MAT_DIALOG_DATA) public data: ReportsDialogData,
  ) {
    this.report = {
      ...data.report,
      media: [...data.report.media],
      responses: [...data.report.responses],
    };
  }

  ngOnInit(): void {
    if (this.canAssign) {
      this.reportService.getAssignableUsers().subscribe({
        next: (users) => {
          this.assignableUsers = users;
          this.cdr.markForCheck();
        },
        error: (err) => console.error('getAssignableUsers failed:', err),
      });
    }
  }

  /**
   * Returns the current authenticated user from UserService.
   * @returns {User | null}
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  categoryIcon(): string {
    return CATEGORY_ICON[this.report.category];
  }

  categoryClass(): string {
    return CATEGORY_CLASS[this.report.category];
  }

  private assignedUser(): AssignableUser | undefined {
    if (this.report.assignedTo === null || this.report.assignedTo === undefined) {
      return undefined;
    }
    return this.assignableUsers.find((u) => u.id === Number(this.report.assignedTo));
  }

  assigneeLabel(): string {
    if (this.report.assignedTo === null || this.report.assignedTo === undefined) {
      return 'Unassigned';
    }
    const user = this.assignedUser();
    if (user) {
      return assignableUserLabel(user);
    }
    return this.report.assigneeName ?? `User #${this.report.assignedTo}`;
  }

  assigneeInitials(): string {
    if (this.report.assignedTo === null || this.report.assignedTo === undefined) {
      return '';
    }
    const user = this.assignedUser();
    if (user) {
      const first = (user.preferred_name || user.first_name || '')[0] ?? '';
      const last = (user.last_name || '')[0] ?? '';
      return `${first}${last}`.toUpperCase() || `U${this.report.assignedTo}`;
    }
    const name = this.report.assigneeName;
    if (name) {
      const parts = name.split(' ').filter(Boolean);
      const initials = parts
        .map((p) => p[0])
        .join('')
        .slice(0, 2);
      if (initials) return initials.toUpperCase();
    }
    return `U${this.report.assignedTo}`.slice(0, 2).toUpperCase();
  }

  /**
   * Returns the assigned user's saved avatar image, or null if they have
   * none saved (or the assignee isn't resolvable), so the template can
   * fall back to initials via app-user-avatar.
   * @returns {string | null}
   */
  assigneeImageUrl(): string | null {
    return this.assignedUser()?.profile_picture_path ?? null;
  }

  submitterLabel(): string {
    return `Staff #${this.report.submittedBy}`;
  }

  get canAssign(): boolean {
    return this.currentUser?.hasAnyPermission([this.permissions.REPORTS.ASSIGN]) ?? false;
  }

  assignableLabel(user: AssignableUser): string {
    return assignableUserLabel(user);
  }

  changeStatus(status: ReportStatus): void {
    if (status === this.report.status || this.statusUpdating) {
      return;
    }

    this.statusUpdating = true;
    this.reportService.updateStatus(this.report.id, status).subscribe({
      next: (updated) => {
        this.applyUpdatedReport(updated);
        this.statusUpdating = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('updateStatus failed:', err);
        this.statusUpdating = false;
        this.cdr.markForCheck();
      },
    });
  }

  changeAssignee(assignedTo: number | null): void {
    const current = this.report.assignedTo ?? null;
    const next = assignedTo ?? null;
    if (next === current || this.assigneeUpdating) {
      return;
    }

    this.assigneeUpdating = true;
    this.reportService.assignUser(this.report.id, next).subscribe({
      next: (updated) => {
        this.applyUpdatedReport(updated);
        this.assigneeUpdating = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('assignUser failed:', err);
        this.assigneeUpdating = false;
        this.cdr.markForCheck();
      },
    });
  }

  private applyUpdatedReport(updated: IssueReport): void {
    this.report = {
      ...updated,
      media: [...updated.media],
      responses: [...updated.responses],
    };
    this.reportChanged = true;
  }

  close(): void {
    this.dialogRef.close(this.reportChanged ? this.report : undefined);
  }

  attachmentPath(path: string): string {
    return toFileUrl(path) ?? '';
  }
}
